import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  MessageCircle,
  X,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Heart,
  PartyPopper,
} from 'lucide-react';
import WelcomeBackPanel from './guest/WelcomeBackPanel';
import ThankYouPanel from './guest/ThankYouPanel';
import QuestionPanel from './guest/QuestionPanel';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * GuestEngagementWidget - collapsible chat-style widget that gathers anonymous
 * guest answers + invites them to join. Recognizes returning visitors.
 */
const GuestEngagementWidget = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [guestId, setGuestId] = useState(null);
  const [guestProfile, setGuestProfile] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [textInput, setTextInput] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [thankYouShown, setThankYouShown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isReturningVisitor, setIsReturningVisitor] = useState(false);
  const [visitCount, setVisitCount] = useState(1);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);

  const fetchGuestProfile = async (gid) => {
    try {
      const response = await fetch(`${API}/api/guest/profile/${gid}`);
      if (response.ok) {
        const data = await response.json();
        setGuestProfile(data);
      }
    } catch (error) {
      console.error('Error fetching guest profile:', error);
    }
  };

  const fetchQuestions = useCallback(async () => {
    if (!guestId) return;
    try {
      const response = await fetch(
        `${API}/api/guest/questions?guest_id=${guestId}&count=3`
      );
      if (response.ok) {
        const data = await response.json();
        setQuestions(data.questions);
        setHasMore(data.has_more);
        setCurrentQuestionIndex(0);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  }, [guestId]);

  // Initialize guest tracking
  useEffect(() => {
    const initGuest = async () => {
      const existingGuestId = localStorage.getItem('pawsitively_guest_id');
      try {
        const response = await fetch(`${API}/api/guest/track-visit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ guest_id: existingGuestId }),
        });
        if (response.ok) {
          const data = await response.json();
          setGuestId(data.guest_id);
          setVisitCount(data.visit_count || 1);
          setIsReturningVisitor(data.returning);
          localStorage.setItem('pawsitively_guest_id', data.guest_id);
          if (data.returning && data.guest_id) {
            fetchGuestProfile(data.guest_id);
            setShowWelcomeBack(true);
          }
        }
      } catch (error) {
        console.error('Error initializing guest:', error);
      }
    };
    initGuest();
  }, []);

  // Fetch questions once we have a guestId and are not in welcome-back mode
  useEffect(() => {
    if (guestId && !showWelcomeBack) {
      fetchQuestions();
    }
  }, [guestId, showWelcomeBack, fetchQuestions]);

  const submitAnswer = async (questionId, answer) => {
    if (!guestId) return;
    setLoading(true);
    try {
      await fetch(`${API}/api/guest/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guest_id: guestId,
          question_id: questionId,
          answer: Array.isArray(answer) ? answer.join(', ') : answer,
        }),
      });

      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex((prev) => prev + 1);
      } else if (hasMore) {
        await fetchQuestions();
      } else {
        setThankYouShown(true);
        localStorage.setItem('pawsitively_widget_seen', 'true');
      }
      setTextInput('');
      setSelectedOptions([]);
    } catch (error) {
      console.error('Error submitting answer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOptionSelect = (option, isMulti) => {
    if (isMulti) {
      setSelectedOptions((prev) =>
        prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
      );
    } else {
      const q = questions[currentQuestionIndex];
      submitAnswer(q.id, option);
    }
  };

  const handleMultiSubmit = () => {
    if (selectedOptions.length === 0) return;
    const q = questions[currentQuestionIndex];
    submitAnswer(q.id, selectedOptions);
  };

  const handleTextSubmit = () => {
    if (!textInput.trim()) return;
    const q = questions[currentQuestionIndex];
    submitAnswer(q.id, textInput.trim());
  };

  const skipQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else if (hasMore) {
      fetchQuestions();
    } else {
      setThankYouShown(true);
    }
    setTextInput('');
    setSelectedOptions([]);
  };

  const closeWidget = () => {
    setIsOpen(false);
    localStorage.setItem('pawsitively_widget_seen', 'true');
  };

  const currentQuestion = questions[currentQuestionIndex];

  // Nothing to show yet
  if (questions.length === 0 && !thankYouShown && !showWelcomeBack) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50" data-testid="guest-engagement-widget">
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="rounded-full w-14 h-14 shadow-lg bg-primary hover:bg-primary/90"
          data-testid="open-engagement-widget"
        >
          {isReturningVisitor ? (
            <Heart className="w-6 h-6" />
          ) : (
            <MessageCircle className="w-6 h-6" />
          )}
        </Button>
      )}

      {isOpen && (
        <Card
          className={`w-80 shadow-2xl border-2 transition-all duration-300 ${
            isMinimized ? 'h-14' : ''
          }`}
        >
          <div
            className="flex items-center justify-between p-3 bg-primary text-primary-foreground rounded-t-lg cursor-pointer"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            <div className="flex items-center gap-2">
              {showWelcomeBack ? (
                <PartyPopper className="w-5 h-5" />
              ) : (
                <Sparkles className="w-5 h-5" />
              )}
              <span className="font-medium text-sm">
                {showWelcomeBack ? 'Welcome Back!' : 'Quick Question'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-primary-foreground hover:bg-white/20"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMinimized(!isMinimized);
                }}
              >
                {isMinimized ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-primary-foreground hover:bg-white/20"
                onClick={(e) => {
                  e.stopPropagation();
                  closeWidget();
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {!isMinimized && (
            <CardContent className="p-4">
              {showWelcomeBack ? (
                <WelcomeBackPanel
                  visitCount={visitCount}
                  guestProfile={guestProfile}
                  navigate={navigate}
                  onContinueQuestions={() => {
                    setShowWelcomeBack(false);
                    fetchQuestions();
                  }}
                />
              ) : thankYouShown ? (
                <ThankYouPanel navigate={navigate} />
              ) : (
                <QuestionPanel
                  question={currentQuestion}
                  loading={loading}
                  selectedOptions={selectedOptions}
                  textInput={textInput}
                  setTextInput={setTextInput}
                  onOptionSelect={handleOptionSelect}
                  onMultiSubmit={handleMultiSubmit}
                  onTextSubmit={handleTextSubmit}
                  onSkip={skipQuestion}
                />
              )}

              {!thankYouShown && !showWelcomeBack && questions.length > 0 && (
                <div className="flex gap-1 mt-3 justify-center">
                  {questions.map((q, idx) => (
                    <div
                      key={`progress-${q.id || idx}`}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx < currentQuestionIndex
                          ? 'bg-primary'
                          : idx === currentQuestionIndex
                          ? 'bg-primary/50'
                          : 'bg-muted'
                      }`}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};

export default GuestEngagementWidget;
